import {
  createContext,
  useContext,
  useState,
  useEffect,
  FC,
  ReactNode,
} from 'react';
import { errorToast } from '../../utils/helper';
import {
  GetUsersResponse,
  CreatePushSubscriptionRequest,
} from '@inplay/models';
import { UserService } from '../userService';
import { PushNotificationService } from '../pushNotificationService';
import { useAuth } from './authContext';
import { useNotificationService } from './notificationContext';

type UserDetailContextType = {
  userDetail: GetUsersResponse | undefined;
  userDetailLoading: boolean;
  updateUserDetail: (updatedData: Partial<GetUsersResponse>) => void;
};

const UserDetailContext = createContext<UserDetailContextType | undefined>(
  undefined,
);

export const UserDetailProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const pushNotificationService = new PushNotificationService();
  const { subscription, isActiveSubscription } = useNotificationService();

  const [userDetail, setUserDetail] = useState<GetUsersResponse>();
  const [userDetailLoading, setUserDetailLoading] = useState<boolean>(false);

  const { accessToken } = useAuth();

  useEffect(() => {
    if (!accessToken) {
      setUserDetail(undefined);
      return;
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    const fetchUserDetail = async () => {
      setUserDetailLoading(true);
      try {
        console.log(
          `[userDetailContext][fetchUserDetail] Before calling detail...`,
        );
        const service: UserService = new UserService();
        const getUserDetailsResponse: GetUsersResponse =
          await service.getUserDetail();

        if (subscription && !isActiveSubscription) {
          const subscriptionRequest: CreatePushSubscriptionRequest = {
            userId: getUserDetailsResponse.id,
            organizationId: getUserDetailsResponse.organization.id,
            subscription: subscription,
          };
          await pushNotificationService.createPushSubscription(
            subscriptionRequest,
          );
        }

        setUserDetail(getUserDetailsResponse);
      } catch (error: unknown) {
        console.error(`[userDetailContext](fetchUserDetail)`, { error });
        errorToast(error);
        console.error('[userDetailContext] Error fetching data:', error);
      } finally {
        setUserDetailLoading(false);
      }
    };

    fetchUserDetail();
  }, [accessToken, subscription]);

  // The new function to update userDetail locally
  const updateUserDetail = (updatedData: Partial<GetUsersResponse>) => {
    setUserDetail((prevUser) => {
      if (!prevUser) return prevUser;
      return { ...prevUser, ...updatedData };
    });
  };

  return (
    <UserDetailContext.Provider
      value={{ userDetail, userDetailLoading, updateUserDetail }}
    >
      {children}
    </UserDetailContext.Provider>
  );
};

export const useUserDetailContext = () => {
  const context = useContext(UserDetailContext);
  if (!context)
    throw new Error('userDetail must be used within UserDetail provider');
  return context;
};
